import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useQuery } from 'react-query';
import CancerDetailsForm from './CancerDetailsForm';
import useInput from '../../hooks/use-input';
import { uiActions } from '../../store/ui-slice';
import {
	createBillingGridRequest,
	getFormOptionslist
} from '../../api/studiesAPI';
import Loader from '../ui/Loader';
import Button from '../ui/Button';

import './DiseaseTypeForm.scss';

const newTableRowDefaultData = {
	cancerType: '',
	cancerSubtypes: '',
	cancerStages: '',
	biomarkers: ''
};

const DiseaseTypeForm = ({
	studyId,
	fileName,
	onEndProcess,
	onQCTform
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [tableRows, setTableRows] = useState([
		newTableRowDefaultData
	]);

	const [validForm, setValidForm] = useState(false);
	const [errorInfoMessage, setErrorInfoMessage] =
		useState('');

	const {
		value: enteredPageSelection,
		isValid: enteredPageSelectionIsValid,
		hasError: pageSelectionInputHasError,
		valueChangeHandler: pageSelectionChangedHandler,
		inputBlurHandler: pageSelectionBlurHandler,
		reset: resetPageSelectionInput
	} = useInput((value) => value.trim() !== '');

	const {
		value: enteredObjPageSelection,
		isValid: enteredObjPageSelectionIsValid,
		hasError: objPageSelectionInputHasError,
		valueChangeHandler: objPageSelectionChangedHandler,
		inputBlurHandler: objPageSelectionBlurHandler,
		reset: resetObjPageSelectionInput
	} = useInput((value) => value.trim() !== '');

	const { isLoading, isError, error, data } = useQuery({
		queryKey: ['form-options'],
		queryFn: getFormOptionslist,
		retry: false,
		staleTime: 3000000 //5 minutes to refetch
	});
	if (isLoading) {
		return <Loader />;
	}
	const { types, subtypes } = data.data.types;

	const newTableRowHandler = () => {
		setTableRows([...tableRows, newTableRowDefaultData]);
		validateRowsInfo([
			...tableRows,
			newTableRowDefaultData
		]);
	};

	const handleTableRowChange = (rowFormData, index) => {
		const currentRows = [...tableRows];
		currentRows[index] = rowFormData;
		setTableRows(currentRows);
		validateRowsInfo(currentRows);
	};

	const handleProtocolInfoProcessError = (studyId) => {
		dispatch(
			uiActions.launchNotification({
				title: `The Protocol Details could not be added`,
				message:
					'An unexpected error has ocurred, please try again!',
				status: 'error'
			})
		);
		navigate(`/dashboard/${studyId}`);
	};

	const deleteRowHandler = (index) => {
		setTableRows((prev) =>
			prev.filter((_, i) => index !== i)
		);
	};
	//Todo add search
	const gatherProtocolFormData = (event) => {
		event.preventDefault();
		const infoIsValid = tableRows.map((item) => {
			if (
				!item.cancerType ||
				!item.cancerSubtypes ||
				!item.cancerStages ||
				!item.biomarkers
			) {
				return false;
			} else {
				return true;
			}
		});

		if (infoIsValid.every((value) => value === true)) {
			createBillingGridRequest({
				studyId: studyId,
				fileName: fileName,
				table: tableRows,
				page: enteredPageSelection,
				objectivesPage: enteredObjPageSelection
			})
				.then((result) => {
					console.log(result);
				})
				.catch((error) => {
					console.log(error);
					handleProtocolInfoProcessError(studyId);
				});

			onEndProcess();
			if (onQCTform) {
				onQCTform();
			}
			resetPageSelectionInput();
			resetObjPageSelectionInput();
			setErrorInfoMessage('');
		} else {
			setValidForm(false);
			setErrorInfoMessage(
				'Please complete all information'
			);
		}
	};

	let formIsValid = false;

	if (
		enteredPageSelectionIsValid &&
		enteredObjPageSelectionIsValid
	) {
		formIsValid = true;
	}

	const validateRowsInfo = (currentRows) => {
		currentRows.map((item) => {
			if (
				item.cancerType !== '' &&
				item.cancerSubtypes !== '' &&
				item.cancerStages !== '' &&
				item.biomarkers !== ''
			) {
				return setValidForm(true);
			}

			return setValidForm(false);
		});
	};

	const pageInputClasses = pageSelectionInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	const pageObjInputClasses = objPageSelectionInputHasError
		? 'form-control is-invalid'
		: 'form-control';

	return (
		<div>
			<form className='add-data-form'>
				<div className='form-title'>
					<h5>Add Protocol Details</h5>
				</div>
				<div className='form-subtitle'>
					<p>
						Please help our software increase its accuracy
						by providing the protocol information below.
						Including the page location and page orientation
						details on the PDF.
					</p>
					<p>
						Note: For multiple cancer types for one same
						study, add another row.
					</p>
					<p>* Mandatory fields</p>
				</div>
				<div className='control-container row'>
					<div className='col-sm-6'>
						<label>
							Study Objectives Page Number(s){' '}
							<span className='mandatory-field'>*</span>
						</label>
						<input
							placeholder='i.e: 3,12,15'
							className={pageObjInputClasses}
							onChange={objPageSelectionChangedHandler}
							onBlur={objPageSelectionBlurHandler}
						/>
						{objPageSelectionInputHasError && (
							<p className='error-text'>
								Study Objectives Page must not be empty.
							</p>
						)}
					</div>
					<div className='col-sm-6'>
						<label>
							Schedule of Assessments Page Number(s){' '}
							<span className='mandatory-field'>*</span>
						</label>
						<input
							placeholder='i.e: 3,12,15'
							className={pageInputClasses}
							onChange={pageSelectionChangedHandler}
							onBlur={pageSelectionBlurHandler}
						/>
						{pageSelectionInputHasError && (
							<p className='error-text'>
								Schedule of Assessments Page must not be
								empty.
							</p>
						)}
					</div>
				</div>
			</form>
			<table className='table table-bordered  table-hover'>
				<thead className='table-secondary'>
					<tr>
						<th>Cancer Type *</th>
						<th>Cancer Subtypes</th>
						<th>Cancer Stage</th>
						<th>Biomarkers</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					{tableRows.map((tableRow, i) => (
						<CancerDetailsForm
							key={Math.random()}
							types={types}
							subtypes={subtypes}
							tableRow={tableRow}
							onTableRowChange={handleTableRowChange}
							index={i}
							onDeleteRow={deleteRowHandler}
						/>
					))}
				</tbody>
			</table>
			<div className='add-row-sec-button-container'>
				{' '}
				<Button
					className='add-row-sec-button'
					onClick={newTableRowHandler}
				>
					+ New Row
				</Button>
			</div>
			{errorInfoMessage && (
				<p className='error-text'>{errorInfoMessage}</p>
			)}
			<div className='send-protocol-info-button'>
				<Button
					onClick={gatherProtocolFormData}
					className='primary-button'
					disabled={!formIsValid || !validForm}
				>
					Send Protocol Info
				</Button>
			</div>
		</div>
	);
};

export default DiseaseTypeForm;
